/**
 * React Query-backed feature flag hook.
 *
 * Resolves which optional modules (today: voice; later: whatsapp, rcs)
 * are enabled for the current tenant. The backend AND-s the global env
 * gate (e.g. `VOICE_ENABLED`) with the per-tenant column (`feature_voice`)
 * so each boolean here is the effective value — the frontend never has
 * to know about the env layer.
 *
 * Cached for 5 minutes since flag flips by platform admin are rare.
 * Components gate sidebar items, lazy-loaded routes, and feature-specific
 * UI behind `useFeature('voice')` for a tight one-liner check.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface Features {
  voice: boolean;
  /** Per-tenant SMS module gate (mig 097). Default true. Voice-only
   *  customers set false so the portal hides SMS sidebar items. */
  sms: boolean;
}

const FEATURES_QUERY_KEY = ['features'] as const;
const FEATURES_STALE_TIME_MS = 5 * 60 * 1000;

async function fetchFeatures(): Promise<Features> {
  return api.get<Features>('/v1/features');
}

/**
 * Returns the full feature map. Use this when you need to check more than
 * one flag in the same component.
 *
 * `features` is `undefined` while the initial fetch is in flight; treat
 * that as "feature off" in gating code so the UI defaults to safe-disabled
 * during the first render.
 */
export function useFeatures() {
  return useQuery({
    queryKey: FEATURES_QUERY_KEY,
    queryFn: fetchFeatures,
    staleTime: FEATURES_STALE_TIME_MS,
    // The features endpoint is not user-input dependent and the cost of a
    // wrong answer (rendering a hidden link) is small — no need for
    // refetchOnWindowFocus / refetchOnMount.
    refetchOnWindowFocus: false,
  });
}

/**
 * Convenience wrapper for the common single-flag check. Returns `false`
 * during the initial fetch and on error so gating code defaults to off.
 *
 * Example:
 *   const voiceOn = useFeature('voice');
 *   {voiceOn && <SidebarLink to="/voice/flows" />}
 */
export function useFeature(name: keyof Features): boolean {
  const { data } = useFeatures();
  return data?.[name] ?? false;
}
